import { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { api } from '../../lib/api';
import { useToast, writeWithToast, SplitRow } from '../common';
import { useNav, dayStatNav, attentionNav } from '../../pages/NavContext';
import { useWindowedBookings } from '../bookings/useWindowedBookings';
import { revalidateBooking } from '../bookings/revalidateBooking';
import { deskPayAction, sameWindow, quarterWindow, dayStatWindows, dogsLeavingCount, daysInWindow } from '../bookings/domain';
import { isoDate, addDays } from '../../lib/dates';
import { useMediaQuery, PHONE_QUERY } from '../../lib/useMediaQuery';
import { BookingsCharts } from '../bookings/BookingsCharts';
import { MoneyChart } from './MoneyChart';
import { useDailyMoney } from './useDailyMoney';
import { ChartDropdown } from '../bookings/ChartDropdown';
import { DAILY_CHART_GROUPS, CHART_GROUPS, CHART_TYPES } from '../bookings/constants';
import { OwnerBookingModal } from '../owners/OwnerBookingModal';
import type { BookingStatus, ChartGroup, ChartType } from '../bookings/types';
import { useStaff } from '../staff/useStaff';
import { useShifts } from '../staff/useShifts';
import { StaffModal } from '../staff/StaffModal';
import type { Staff } from '../staff/types';
import { arrivingOn, collectingOn, inResidenceOn, residenceCount, onDutyTeams } from './today';
import { useArrivedResidents } from '../bookings/useArrivedResidents';
import { useBookingStats } from '../bookings/useBookingStats';
import { TodaySkeleton } from './TodaySkeleton';
import { DeskSection } from './DeskSection';
import { DeskRow } from './DeskRow';
import { CopyBriefButton } from './CopyBriefButton';
import { CommandStrip, type StripPanel } from './CommandStrip';
import { AttentionPanel } from './AttentionPanel';
import { ResidencePanel } from './ResidencePanel';
import { WeekPanelData } from './WeekPanelData';
import { attentionBookings, deriveNeedsAttention, lookaheadWindows } from './needsAttention';
import { useViewings } from './useViewings';
import { expectedInWindow, overlayViewingStatus, viewingsOnDay, windowCovers } from './viewings';
import { bookRoute, dogsForViewing, linkBooking, recordHousehold, viewingToOwnerDefaults, type OwnerFormDog } from '../viewings/convert';
import type { Viewing, ViewingStatus } from '@bbbk/schema';
import { buildOnDutyMessage } from './onDutyMessage';
import { applyPending, settlePending, clearPendingField, type PendingMap, type PendingWrite } from './optimisticDesk';
import { haptic } from '../../lib/haptics';

interface TodayViewProps {
  anchor: string; // selected day, ISO YYYY-MM-DD
  // Booking + owner CREATE now come from the global top-bar New menu
  // (CreateModalProvider); this view only owns the EDIT flow — clicking a desk
  // row opens that booking.
}

// The Today (daily dashboard) body — the day's desk over a context row. Reads one
// windowed bookings fetch (anchor ±2) plus the day's shifts/staff. Drop-offs and
// collections are the working desk; On-duty and In-residence are at-a-glance
// context. Clicking a desk row opens the booking modal; the status + paid actions
// write through and revalidate the window. Everything recomputes for `anchor`.
export function TodayView({ anchor }: TodayViewProps) {
  const today = isoDate(new Date());
  const isLiveToday = anchor === today;
  const nav = useNav();

  // Live clock — re-render each minute so the desk windows light up at 09:00 /
  // 17:00. Window indicators only apply to the real today.
  const [now, setNow] = useState(() => new Date());
  const toast = useToast();
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const hour = now.getHours();
  const inDropoffWindow = isLiveToday && hour >= 9 && hour < 10;
  const inCollectionWindow = isLiveToday && hour >= 17 && hour < 18;

  // ±2-day window + adjacent prefetch — shared helper so Daily and Messages
  // fetch the same shape (stepping resolves from cache, no per-step network wait).
  const { range: statRange, prefetch: prefetchRanges } = useMemo(() => dayStatWindows(anchor), [anchor]);
  const {
    bookings,
    reload,
    loading: bookingsLoading,
    isPending,
    window: shownWindow,
  } = useWindowedBookings(statRange, prefetchRanges);

  // The Needs attention bar reads a SEVEN-DAY LOOK-AHEAD, which the ±2-day desk
  // window cannot supply. It is a second call to the same windowed hook rather
  // than a bespoke fetch: /bookings/calendar already embeds each dog's
  // vaccines_due and the owner's contacts (embedBookings), and the hook already
  // owns the caching, the on-return revalidation and the hold-across-refetch —
  // none of which is worth writing twice. It carries its own loading flag so a
  // slow look-ahead never holds up the desk.
  const { range: aheadRange, prefetch: aheadPrefetch } = useMemo(() => lookaheadWindows(anchor), [anchor]);
  const {
    bookings: aheadBookings,
    window: aheadWindow,
    loading: aheadLoadingRaw,
    failed: aheadFailed,
  } = useWindowedBookings(aheadRange, aheadPrefetch);

  // DERIVE AGAINST THE WINDOW THE DATA IS FOR, never the one just requested.
  //
  // The hook holds the previous window's bookings until the new one lands. The
  // first attempt at Codex's P2 blanked the bar for that interval, which was
  // correct and horrible: every date step flashed an empty bar for 300–470ms
  // while every other card on the page held its content. Pairing the held
  // bookings with their OWN window is self-consistent — window A's stays are
  // never filtered through window B's dates — so the bar can hold like its
  // neighbours AND still never mismatch. `aheadWindow.from` IS the old anchor
  // while a step is in flight, and becomes the new one the moment data lands.
  const aheadUnknown = aheadLoadingRaw || aheadFailed;
  // Held items that are not yet for the requested window. Not a reason to blank
  // — that was the flicker — but a reason to say so.
  const aheadStale = !aheadUnknown && !sameWindow(aheadWindow, aheadRange);
  // The date the held look-ahead is ANCHORED to. `aheadWindow` is null until a
  // first response lands, which is exactly when `aheadUnknown` is true and both
  // derivations below short-circuit to empty — so the fallback names a date that
  // is never actually read. It exists because the compiler cannot see that.
  const aheadAnchor = aheadWindow?.from ?? aheadRange.from;
  // The window's bookings alone: requests left Needs attention for the stack beside "Daily"
  // (Freddi, 2026-10-14), so the extra read of every open request went with them.
  const attentionSource = aheadBookings;
  const needsAttention = useMemo(
    () => (aheadUnknown ? [] : deriveNeedsAttention(attentionSource, aheadAnchor)),
    [attentionSource, aheadAnchor, aheadUnknown],
  );

  // The desk (drop-offs/collections) is the ±2-day windowed set — date-relevant
  // to the anchor. The In-residence card is NOT: for the live today it reads the
  // full arrived set (date-unbounded), so an uncollected overstay outside the
  // window still appears and Today agrees with the Bookings 'arrived' chip (the
  // 54/55 fix). Other days use the windowed set's date-based projection.
  const { bookings: arrivedResidents, loading: arrivedLoading, reload: reloadArrived } =
    useArrivedResidents(isLiveToday);
  const residenceBookings = isLiveToday ? arrivedResidents : bookings;

  // The live count comes from the UNCAPPED server arrived stat — the very same
  // /bookings/stats the Bookings 'arrived' chip reads, so the two are identical
  // by construction and the count is never limited by pagination. Only fetched on
  // the real today (the projection days compute their count from occupancy).
  const { stats: liveStats } = useBookingStats(null, null, isLiveToday);

  // VIEWINGS share the desk's own ±2-day window, so stepping the date resolves
  // one from the same cache the bookings come from. `failed` is carried rather
  // than swallowed: a desk that quietly shows no viewings because the request
  // fell over is the same lie as a bar claiming "nothing to chase" before the
  // look-ahead has landed.
  const {
    viewings: allViewings,
    window: viewingsWindow,
    failed: viewingsFailed,
    reload: reloadViewings,
  } = useViewings(statRange.from, statRange.to, prefetchRanges);
  // UNRESOLVED, NOT EMPTY — and a failure is only half of that. A cold load, a
  // slow request, or a date jump outside the held window all leave the set empty
  // with `failed` still false, and the desk would say "No drop-offs scheduled"
  // about a question nobody had answered yet (codex, PR #172).
  //
  // `window` is the honest signal: it names the range the held viewings are FOR,
  // and is null until a first response lands. Comparing it to the range actually
  // requested catches the held-but-stale case as well as the never-loaded one —
  // the same shape the look-ahead bookings already use, for the same reason.
  // A held window that still COVERS the anchor is a true answer for it — a
  // one-day step lands inside the ±2-day read already on hand. Comparing the
  // whole window marked the viewings "not read" for the frames until the new
  // read landed, a flicker on every step (Sir, 2026-09-02). A jump beyond the
  // held window still says so, honestly, until its own read lands.
  const viewingsUnresolved = viewingsFailed || !windowCovers(viewingsWindow, anchor);
  // The optimistic overlay for a viewing's status — the pill flips the instant
  // it is pressed and the PATCH runs behind it, exactly as a booking's does.
  const [pendingViewings, setPendingViewings] = useState<Record<string, ViewingStatus>>({});
  // The viewings whose PATCH has not come back yet. An overlay is only the
  // server's business once its own write has returned; until then a reload is
  // answering a question asked before the press.
  const inFlightViewings = useRef<Set<string>>(new Set());
  const viewingsToday = useMemo(
    () => overlayViewingStatus(viewingsOnDay(allViewings, anchor), pendingViewings),
    [allViewings, anchor, pendingViewings],
  );
  // THE OVERLAY SETTLES ON A FRESH ANSWER, whatever that answer is.
  //
  // It used to clear only when the server AGREED with it, which is fine until
  // two writes race: press the pill, then set a different status in the modal
  // before the first PATCH lands. The reload then carries the modal's value,
  // the overlay disagrees with it for ever, and the row shows the wrong status
  // with a permanently disabled pill — `busyStatus` is keyed on the overlay
  // existing (codex + claude, PR #172).
  //
  // A reload that arrives AFTER the write it was told about is the authority.
  // The write's own id is the ticket: `settled` records which overlay each
  // request owns, and any response that arrives once that request has returned
  // clears it — agreeing or not.
  useEffect(() => {
    setPendingViewings((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const v of allViewings) {
        if (next[v.id] !== undefined && !inFlightViewings.current.has(v.id)) {
          delete next[v.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [allViewings]);

  // THE BAR READS A SEVEN-DAY WINDOW, which the ±2-day desk window cannot
  // supply — the same reason the look-ahead bookings are a second call to the
  // same hook rather than a bespoke fetch. It carries its own failure flag so a
  // slow or failed viewings read never holds up the desk.
  const {
    viewings: aheadViewings,
    window: aheadViewingsWindow,
    failed: aheadViewingsFailed,
    reload: reloadAheadViewings,
  } = useViewings(aheadRange.from, aheadRange.to, aheadPrefetch);
  // TWO READS BEHIND THE BAR, ONE MOUTH. The bar's `loading` and `stale` used to
  // come from the bookings read alone, and this read was collapsed into an
  // empty array when it had not answered — and an empty array cannot say "not
  // read yet". So the bar showed the tick, "Nothing to chase…" and the
  // reassuring title while the viewings request was in flight, after it had
  // failed, and on a date step with LAST week's viewing counted against this
  // one (review, PR #173). The same three states the bookings read already
  // distinguishes, in the same shape, OR-ed into the flags the bar reads:
  //   unknown — never landed, or failed: the whole bar holds its shape and says
  //             nothing (Sir, 2026-09-02: not the bookings counters with a
  //             footnote);
  //   stale   — held for a previous window: shown, dimmed, all-clear withheld.
  // The count is DERIVED AGAINST THE WINDOW THE DATA IS FOR, never the one just
  // requested; a held set filtered through the new dates would count the wrong
  // week undimmed.
  const aheadViewingsUnknown = aheadViewingsFailed || aheadViewingsWindow === null;
  const aheadViewingsStale = !aheadViewingsUnknown && !sameWindow(aheadViewingsWindow, aheadRange);
  const attentionUnknown = aheadUnknown || aheadViewingsUnknown;
  const attentionStale = !attentionUnknown && (aheadStale || aheadViewingsStale);
  // The desk's optimistic overlay applies here too: Visited pressed on the desk
  // drops the viewing from the count at once, as the pill already flipped.
  const chaseViewings = useMemo(
    () =>
      attentionUnknown || !aheadViewingsWindow
        ? []
        : expectedInWindow(
            overlayViewingStatus(aheadViewings, pendingViewings),
            aheadViewingsWindow.from,
            aheadViewingsWindow.to,
          ),
    [aheadViewings, aheadViewingsWindow, attentionUnknown, pendingViewings],
  );
  // Both viewings reads answer the same write. The mutation bus revalidates the
  // look-ahead silently anyway; reloading it here as well means the bar and
  // the panel settle on the same round trip as the desk rather than the next.
  const reloadAllViewings = () => {
    reloadViewings();
    reloadAheadViewings();
  };

  // In-flight desk writes, laid over the fetched set so a click shows instantly.
  // Each entry clears itself the moment the server reports the same value —
  // holding it until then is what stops a row flicking back to its old state
  // between the PATCH returning and the reload landing.
  const [pending, setPending] = useState<PendingMap>({});
  const shownBookings = useMemo(() => applyPending(bookings, pending), [bookings, pending]);
  useEffect(() => {
    setPending((prev) => settlePending(bookings, prev));
  }, [bookings]);

  const arriving = useMemo(() => arrivingOn(shownBookings, anchor), [shownBookings, anchor]);
  const collecting = useMemo(() => collectingOn(shownBookings, anchor), [shownBookings, anchor]);
  // The stays in the block — whole booking rows, for the shared stay-table.
  const residents = useMemo(
    () => inResidenceOn(residenceBookings, anchor, isLiveToday),
    [residenceBookings, anchor, isLiveToday],
  );
  // The arrivals to chase, as rows. Selected by the SAME predicate that produces
  // the rail's counts, so the segment and its panel can never disagree.
  const chaseRows = useMemo(
    () => (aheadUnknown ? [] : attentionBookings(attentionSource, aheadAnchor)),
    [attentionSource, aheadAnchor, aheadUnknown],
  );
  // Live: the uncapped server arrived count (== the chip). Until that stat lands
  // we fall back to the client dog tally over the loaded set — equal for any real
  // kennel, and the cap only ever bounds the avatar LIST, never this number. Any
  // other day: the date-based occupancy projection (== calendar / roster).
  const inResidenceCount = useMemo(() => {
    if (!isLiveToday) return residenceCount(bookings, anchor, false);
    return liveStats?.byStatus.arrived ?? residenceCount(arrivedResidents, anchor, true);
  }, [isLiveToday, liveStats, bookings, arrivedResidents, anchor]);

  // Upcoming-occupancy chart: a forward window that STARTS at the selected day
  // (the date switcher's anchor) and runs +3 months, independent of the desk's
  // ±2-day window. Its own windowed fetch; line/bar is view-state.
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [groupBy, setGroupBy] = useState<ChartGroup>('area');
  const chartRange = useMemo(() => quarterWindow(anchor), [anchor]);

  // PHONE TIER: a quarter at one point per day is 92 columns on a 390px screen —
  // about four pixels each. Only the DISPLAYED window narrows to a fortnight,
  // paged by a horizontal drag across the plot. Desktop is untouched and still
  // shows the whole quarter.
  const phone = useMediaQuery(PHONE_QUERY);
  const CHART_PAGE_DAYS = 14;
  // `quarterWindow` runs from the anchor FORWARD three months, so the swipe was
  // one-directional by construction: page 0 held today and there was nothing
  // behind it. The desk reads backwards as often as forwards — "how full were we
  // last week?" — so the phone's fetch reaches back this many pages as well.
  // Two, not more: one extra month on a request already spanning three, and past
  // that the date picker is the better instrument.
  const CHART_BACK_PAGES = 2;
  // What is FETCHED. Still one request; desktop's is unchanged.
  const chartFetchRange = useMemo(
    () =>
      phone
        ? {
            from: isoDate(addDays(chartRange.from, -CHART_PAGE_DAYS * CHART_BACK_PAGES)),
            to: chartRange.to,
          }
        : chartRange,
    [phone, chartRange],
  );
  // Page 0 is the start of the FETCHED range, so the fortnight holding the
  // anchor sits CHART_BACK_PAGES in. Opening on page 0 would open in the past.
  const [chartPage, setChartPage] = useState(CHART_BACK_PAGES);
  // A new anchor is a new range, so the page returns to the one holding it.
  useEffect(() => {
    setChartPage(phone ? CHART_BACK_PAGES : 0);
  }, [chartRange.from, phone]);
  const chartPages = Math.max(
    1,
    Math.ceil(daysInWindow(chartFetchRange.from, chartFetchRange.to) / CHART_PAGE_DAYS),
  );
  const chartViewWindow = useMemo(() => {
    if (!phone) return chartRange;
    const page = Math.min(Math.max(chartPage, 0), chartPages - 1);
    // The range is not a whole number of fortnights, so the last page would run
    // short — six to nine days drawn at twice the column width of every other
    // page, which reads as a different chart rather than the end of this one.
    // The final page is anchored to the END of the range instead: it overlaps
    // its predecessor rather than shrinking.
    const lastAllowed = chartFetchRange.to;
    let from = isoDate(addDays(chartFetchRange.from, page * CHART_PAGE_DAYS));
    let to = isoDate(addDays(from, CHART_PAGE_DAYS - 1));
    if (to > lastAllowed) {
      to = lastAllowed;
      const anchored = isoDate(addDays(to, -(CHART_PAGE_DAYS - 1)));
      if (anchored > chartFetchRange.from) from = anchored;
    }
    return { from, to };
  }, [phone, chartRange, chartFetchRange, chartPage, chartPages]);
  const pageChart = (dir: 1 | -1) => {
    const next = Math.min(Math.max(chartPage + dir, 0), chartPages - 1);
    // A swipe that hits the end of the range looks identical to a swipe that was
    // fumbled — the bars simply do not move either way. The two are told apart
    // by feel, which is the whole reason the 'bump' pattern is doubled.
    haptic(next === chartPage ? 'bump' : 'page');
    setChartPage(next);
  };
  const {
    bookings: chartBookings,
    isPending: chartPending,
    window: chartShownWindow,
  } = useWindowedBookings(chartFetchRange);
  const chartStepping = chartPending && !sameWindow(chartShownWindow, chartFetchRange);
  // Money per day. Offered only to the roles that can see Finances at all
  // (routeMap gives it to admin + owner): a helper works this desk but does not
  // see the takings, and /api/finances/* 403s them regardless. Gating the option
  // means never offering a door that is locked.
  const canSeeMoney = nav.role === 'admin' || nav.role === 'owner';
  const groupOptions = canSeeMoney ? DAILY_CHART_GROUPS : CHART_GROUPS;
  const showMoney = canSeeMoney && groupBy === 'money';
  // THE SAME WINDOW as the occupancy chart (Freddi, 2026-07-30). Switching the
  // dropdown from "By area" to "By money" must not silently change the period
  // underneath — a reader comparing the two would be comparing different months
  // without being told. It had its own trailing-30-day window while it also drew
  // a receipts series, which genuinely does look backwards; earned value does
  // not. Forward nights here are contracted revenue, not speculation.
  const money = useDailyMoney(chartRange, showMoney);
  // Sliced to the displayed window, exactly as the occupancy series is. The
  // FETCH stays the quarter — one request, unchanged.
  const moneyDays = useMemo(
    () =>
      money.state.data == null
        ? null
        : !phone
          ? money.state.data
          : money.state.data.filter(
              (d) => d.date >= chartViewWindow.from && d.date <= chartViewWindow.to,
            ),
    [money.state.data, phone, chartViewWindow],
  );

  const { state: staffState, reload: reloadStaff } = useStaff();
  const staff = staffState.phase === 'ready' ? staffState.staff : [];
  // Fetch shifts for the same ±2 window as the bookings (not the single day), so
  // stepping one day stays INSIDE the held set — On-duty no longer flashes
  // "No one rostered" while a one-day fetch lands. onDutyTeams filters by anchor.
  const { shifts, loading: shiftsLoading } = useShifts(statRange.from, statRange.to);
  const duty = useMemo(() => onDutyTeams(shifts, staff, anchor), [shifts, staff, anchor]);
  const onDuty = duty.onDuty;

  // Booking modal (open a row → fetch detail → edit; header → create). Owners
  // load lazily as server-driven typeahead once a modal is open. Mirrors
  // BookingsView so Today reuses the exact same modal behaviour.
  // One unified owner↔booking modal — mirrors BookingsView.
  // `createOwner` opens the household form rather than a booking — the route
  // the desk's Book action takes on a viewing. `convertingViewing` is the
  // viewing that route is converting, held so the two writes can find it after
  // the household and the booking land.
  const [modal, setModal] = useState<{
    bookingId: string | null;
    createOwner?: boolean;
    // A booking for a household that already exists — the route Book takes on
    // a viewing that was converted and then had its booking abandoned.
    ownerId?: string;
    initialMode?: 'new';
    convertingViewing?: Viewing;
    // The viewing on the pane — the modal opened from a viewing's row.
    viewing?: Viewing;
  } | null>(null);
  // A viewing opens in the owner↔booking modal, beside the household's search
  // and history (Sir, 2026-09-02) — the same modal a booking opens in.
  const openViewing = (v: Viewing) => setModal({ bookingId: null, viewing: v });
  const [editHelper, setEditHelper] = useState<Staff | null>(null);
  // Which of the strip's three panels is open — one at a time, because two open
  // panels is a page, not a strip.
  const [stripOpen, setStripOpen] = useState<StripPanel>(null);
  // The week panel's offset, and the anchor it is relative to, held TOGETHER.
  //
  // BUG-1 was that the offset outlived the date it was measured from: step the
  // panel to "Next week", move the page date, and the panel showed the week
  // after the new date's week — still labelled relative to the new anchor, so it
  // answered a question nobody had asked. Changing the date is a request to look
  // at that date; the week follows it.
  //
  // The obvious repair is an effect that zeroes the offset when the anchor
  // changes, and that was the first repair. It does not work, and a spec proves
  // it: an effect — even a layout effect — runs after a render has already
  // happened with the NEW anchor and the OLD offset still paired, and the
  // panel's own fetch effect runs off that pair. It asked the server for the
  // week beyond the one on screen and then discarded the answer (PR #164,
  // claude P2; the suggested useLayoutEffect was measured and still asked).
  //
  // So the pair is one piece of state and the offset is DERIVED. A stale pairing
  // is not corrected quickly here — it cannot be constructed.
  const [week, setWeek] = useState({ anchor, offset: 0 });
  const weekOffset = week.anchor === anchor ? week.offset : 0;

  // The week panel's data mounts on FIRST open and stays mounted: fetching a
  // week of shifts and bookings on every Daily load would serve something
  // usually shut, and the reveal cannot animate a child that is not there.
  const weekMounted = useRef(false);
  if (stripOpen === 'duty') weekMounted.current = true;

  const openBooking = (id: string) => setModal({ bookingId: id });

  // The desk's status pill on a viewing. Same shape as onSetStatus below and for
  // the same reasons — optimistic, one write in flight per row, and a failure
  // clears the overlay and refetches rather than leaving the row lying.
  const onSetViewingStatus = (id: string, status: ViewingStatus) => {
    if (pendingViewings[id] !== undefined) return;
    setPendingViewings((prev) => ({ ...prev, [id]: status }));
    // Marked in flight BEFORE the request, cleared when it returns either way —
    // so the reconciliation effect can tell a reload that answers this write
    // from one that was already on its way when the pill was pressed.
    inFlightViewings.current.add(id);
    void writeWithToast(toast, {
      write: async () => {
        try {
          await api(`/viewings/${id}`, { method: 'PATCH', body: { status } });
        } finally {
          inFlightViewings.current.delete(id);
        }
        reloadAllViewings();
      },
      // No success toast — the pill moved before the request landed, so a toast
      // would announce what the eye has just watched happen.
      error: 'Could not update the viewing.',
      revert: () => {
        inFlightViewings.current.delete(id);
        setPendingViewings((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        reloadAllViewings();
      },
    });
  };

  // BOOK — the ONE route into `owners` this feature opens, and the only way a
  // viewing becomes a household.
  //
  // NOTHING IS WRITTEN HERE. Pressing the button opens a form; closing it
  // without saving must leave the viewing exactly as it was, still Expected,
  // with no marker and no link (AC5). The prototype set the status on the press
  // and could not honour that.
  //
  // WHERE IT GOES depends on what the viewing already has: no household yet,
  // the pre-filled create; a household and no booking, a new booking for THAT
  // household. The second used to open the create again and made a duplicate
  // (review, PR #174) — see bookRoute.
  const onBookViewing = (v: Viewing) => setModal(bookRoute(v));

  // THE HOUSEHOLD EXISTS. Booking somebody means they came, so Visited is a
  // consequence rather than a decision to put to the user — and it is written
  // HERE rather than after the booking, so the AC5b ending (household saved,
  // booking abandoned) does not leave a viewing reading Expected about a visit
  // that demonstrably happened. The household's id goes with it, so the next
  // Book press finds it, and the dogs as they were named on the form.
  const onHouseholdCreated = (v: Viewing) => (ownerId: string, dogs: OwnerFormDog[]) => {
    void writeWithToast(toast, {
      write: async () => {
        await recordHousehold(api, v.id, ownerId, dogsForViewing(dogs));
        reloadAllViewings();
      },
      // No success toast — the household's own toast has just fired, and two in
      // a row for one action reads as two things having happened.
      error: 'The household was created, but the viewing could not be marked visited.',
    });
  };

  // THE BOOKING EXISTS. Only now is the link written: the marker and the link
  // render from `booking_id` alone, so writing it any earlier is a visible lie
  // about a booking nobody made (AC8).
  const onBookingCreatedFor = (v: Viewing) => (bookingId: string) => {
    void writeWithToast(toast, {
      write: async () => {
        await linkBooking(api, v.id, bookingId);
        reloadAllViewings();
      },
      error: 'The booking was created, but the viewing could not be linked to it.',
    });
  };
  const closeModal = () => setModal(null);
  // No hard reload() on a modal save — that flashed the desk to its skeleton. The
  // mutation bus (bumped by api() on the write) silently revalidates the windowed
  // bookings in place, so the desk + chart refresh without a flash.
  const onModalChanged = () => {};

  // Status + paid writes, OPTIMISTIC (Freddi, 2026-07-24): the row flips the
  // instant you click — the overlay below is what the desk renders — and the
  // PATCH runs behind it. Previously the click waited on the PATCH *and* the
  // reload, so it felt dead for two round trips.
  //
  // A field with an overlay in flight also DISABLES its control (see the desk
  // rows), so a second press can't race the first — without that, the server
  // could apply two opposite PATCHes out of order and land on the wrong state
  // (PR #147 review). The guard here is the same rule, belt-and-braces.
  //
  // On failure we clear ONLY the failed field (a concurrent toggle on the same
  // row stays optimistic) and reload — cache-bypassing, so if the PATCH
  // actually committed but its response was lost, the desk still ends on the
  // server's truth rather than the pre-write value. A 401 has already
  // redirected, so writeWithToast skips the revert for it.
  const revertField = (id: string, field: keyof PendingWrite) => {
    setPending((prev) => clearPendingField(prev, id, field));
    reload();
  };

  const onSetStatus = (id: string, status: BookingStatus) => {
    if (pending[id]?.status !== undefined) return; // a status write is already in flight
    setPending((prev) => ({ ...prev, [id]: { ...prev[id], status } }));
    void writeWithToast(toast, {
      write: async () => {
        await api(`/bookings/${id}`, { method: 'PATCH', body: { status } });
        // AWAITED, not fire-and-forget: the modal hydrates from this cached read,
        // and opening it straight after a settle was racing the refetch — showing
        // the ledger as it stood before the payment. The desk row has already
        // moved optimistically, so this costs the user nothing visible.
        await revalidateBooking(id);
        reload();
        reloadArrived(); // a status change moves the live in-residence set too
      },
      // No success toast: the row has already moved before the request lands,
      // so a toast would announce something the eye has just watched happen.
      error: 'Could not update the status.',
      revert: () => revertField(id, 'status'),
    });
  };
  // "Paid" on the desk is now a PAYMENT, not a flag. Tapping it settles the
  // outstanding balance dated today; un-tapping removes the desk payment it
  // created and nothing else (a hand-entered deposit is never collateral damage
  // of a mis-tap). The amount is computed server-side from the ledger — the
  // desk's copy of the balance can be seconds stale, and a stale number here
  // would be money invented.
  //
  // The optimistic overlay still moves payment_status, because that is what the
  // row renders; the server recomputes the real verdict from the ledger and the
  // reload below settles any disagreement.
  // One operation key per settle, held until it is undone. Without it the server
  // falls back to "delete the newest desk payment", and on a booking with more
  // than one historical settlement a lost response plus a retry deletes the
  // PRECEDING genuine collection. The keyed server branch existed before this
  // and was never reached, because nothing sent a key.
  const settleKeys = useRef<Record<string, string>>({});
  const onTogglePaid = (id: string) => {
    if (pending[id]?.paymentStatus !== undefined) return; // a payment write is already in flight
    const b = shownBookings.find((x) => x.id === id);
    if (!b) return;
    // The SAME decision the label rendered from, so the click can never do
    // something the button did not offer. A credit is REPORTED, not settled —
    // see the `actionable` guard immediately below; POST /settle collects only.
    const action = deskPayAction(b);
    // A credit is reported, not actioned — the button is disabled, and this is
    // the second lock on the same rule so a stray call can never reach /settle.
    if (!action.actionable) return;
    const settling = action.act !== 'unsettle';
    // A FRESH key on every tap, not a reused one. A booking settled, then
    // stretched so it owes money again, then tapped a second time would
    // otherwise re-send the first key: the server's ON CONFLICT DO NOTHING
    // matches, records nothing, and the desk shows Paid against an outstanding
    // balance. The key is retained only so the UNDO can target the payment this
    // tap created.
    if (settling) settleKeys.current[id] = crypto.randomUUID();
    const key = settleKeys.current[id];
    setPending((prev) => ({ ...prev, [id]: { ...prev[id], paymentStatus: settling ? 'paid' : 'unpaid' } }));
    void writeWithToast(toast, {
      write: async () => {
        await api(
          settling
            ? `/bookings/${id}/settle`
            : `/bookings/${id}/settle${key ? `?clientKey=${key}` : ''}`,
          settling
            ? { method: 'POST', body: { clientKey: key } }
            : { method: 'DELETE' },
        );
        // The tap is undone; the next settle is a new operation.
        if (!settling) delete settleKeys.current[id];
        // AWAITED, not fire-and-forget: the modal hydrates from this cached read,
        // and opening it straight after a settle was racing the refetch — showing
        // the ledger as it stood before the payment. The desk row has already
        // moved optimistically, so this costs the user nothing visible.
        await revalidateBooking(id);
        reload();
      },
      error: 'Could not update the payment.',
      revert: () => revertField(id, 'paymentStatus'),
    });
  };

  // Cold load only: nothing to hold yet on any of the three sources, so show the
  // skeleton instead of painting empty-states. Once landed, the hooks hold their
  // last set across day-steps (isPending dims, never blanks), so this never
  // returns again for a navigation.
  const firstLoad =
    bookingsLoading || shiftsLoading || staffState.phase === 'loading' || arrivedLoading;
  if (firstLoad) return <TodaySkeleton />;

  // Dim ONLY while a different day's window is loading (a real step). A same-window
  // refetch — the silent on-return revalidation (keep-alive activation tick) — must
  // stay invisible, so it is excluded even though it also flips isPending.
  const stepping = isPending && !sameWindow(shownWindow, statRange);

  return (
    <div className={'today-view' + (stepping ? ' is-stepping' : '')}>
      {/* The command strip — what needs chasing, who is working, who is in, on
          one 44px line, each opening into the thing you would otherwise have
          navigated to another page for. */}
      <CommandStrip
        attention={needsAttention}
        attentionViewings={chaseViewings.length}
        attentionLoading={attentionUnknown}
        attentionStale={attentionStale}
        am={duty.am}
        pm={duty.pm}
        residenceCount={inResidenceCount}
        // `busy`, NOT `loading`. Passing `loading` here swapped the whole rail
        // for its skeletons for two frames on every date change — a visible
        // flicker, where every other component on this page holds its content
        // and dims. `busy` only stops a panel opening onto a set that is about
        // to be replaced; the page's own .is-stepping provides the dim.
        busy={stepping}
        open={stripOpen}
        onOpen={setStripOpen}
        panels={{
          attention: (
            <AttentionPanel
              rows={chaseRows}
              items={needsAttention}
              viewings={chaseViewings}
              unknown={attentionUnknown}
              onOpenBooking={openBooking}
              onOpenViewing={openViewing}
              onViewAll={() => attentionNav.openInBookings(nav, anchor)}
            />
          ),
          residence: (
            <ResidencePanel
              rows={residents}
              live={isLiveToday}
              onOpenBooking={openBooking}
              onViewAll={() => dayStatNav.inResidence(nav, anchor)}
            />
          ),
          // The week's data mounts on FIRST open and stays mounted — fetching a
          // week of shifts and bookings on every Daily load would serve
          // something usually shut, and once mounted its region animates like
          // the other two.
          duty: weekMounted.current ? (
            <WeekPanelData
              anchor={anchor}
              offset={weekOffset}
              onStep={(d) => setWeek({ anchor, offset: weekOffset + d })}
              staff={staff}
              today={today}
              // The DISPLAYED week, not the page's anchor — the rota scrolls to the
              // week containing whatever date it is given, so stepping to "Next week"
              // and pressing this opened the original one (PR #164, codex P2).
              onOpenRota={(weekStart) => dayStatNav.onDuty(nav, weekStart)}
              onPickHelper={setEditHelper}
            />
          ) : null,
        }}
      />

      <div className="today-desk">
        <DeskSection
          kind="arrivals"
          title="Drop-offs"
          inWindow={inDropoffWindow}
          bookings={arriving}
          doneStatuses={['arrived', 'collected']}
          viewings={viewingsToday}
          // A read that has not landed is not an empty day, and neither is one
          // that failed (see DeskSection). Both clear themselves as soon as the
          // requested window is the window the data is for.
          viewingsUnknown={viewingsUnresolved}
          renderViewing={(v) => (
            <DeskRow
              key={v.id}
              kind="viewing"
              v={v}
              onOpenViewing={openViewing}
              onSetViewingStatus={onSetViewingStatus}
              onBook={onBookViewing}
              busyStatus={pendingViewings[v.id] !== undefined}
            />
          )}
          renderRow={(b) => (
            <DeskRow
              key={b.id}
              kind="arrival"
              b={b}
              onEditBooking={openBooking}
              onSetStatus={onSetStatus}
              onTogglePaid={onTogglePaid}
              busyStatus={pending[b.id]?.status !== undefined}
              busyPaid={pending[b.id]?.paymentStatus !== undefined}
            />
          )}
        />
        <DeskSection
          kind="collections"
          title="Collections"
          inWindow={inCollectionWindow}
          action={
            /* The message's content is the day's DEPARTURES — "3 dogs leaving
               today, please make sure they're washed" — and Collections IS that
               list. On duty was only ever its host because those are the people
               you send it to. */
            <CopyBriefButton
              text={() => buildOnDutyMessage(anchor, dogsLeavingCount(bookings, anchor), window.location.origin)}
              title="Copy the staff message for today"
              disabled={stepping || !isLiveToday}
              disabledTitle={
                stepping ? 'Loading this day…' : 'Only available on today — the message says "leaving today"'
              }
            />
          }
          bookings={collecting}
          doneStatuses={['collected']}
          renderRow={(b) => (
            <DeskRow
              key={b.id}
              kind="collection"
              b={b}
              onEditBooking={openBooking}
              onSetStatus={onSetStatus}
              onTogglePaid={onTogglePaid}
            />
          )}
        />
      </div>

      <div className="today-chart">
        {/* The chart's group/type selectors reuse the shared SplitRow toolbar
            primitive (right-aligned filters) — same gap + alignment as every
            other page's filter row, no bespoke bar. */}
        <SplitRow
          right={
            <>
              <ChartDropdown label="Group" options={groupOptions} value={groupBy} onChange={setGroupBy} />
              {/* The money view draws one shape — a bar per day — so the type
                  selector has nothing to choose. It stays put, disabled: hiding
                  it reflowed the whole toolbar under the cursor that had just
                  picked "By money". */}
              {/* "Type", not "Chart type" (mobile-prototype-build): the pair has
                  to share one row at phone width, and "Chart type: Status bars"
                  wanted 195px of a 176px pill. The shorter label is what lets
                  both pills keep their prefix instead of one losing it. */}
              <ChartDropdown
                label="Type"
                options={CHART_TYPES}
                value={chartType}
                onChange={setChartType}
                disabled={showMoney}
                disabledHint="The money view draws one shape — a bar per day"
              />
            </>
          }
        />
        {showMoney ? (
          <MoneyChart
            // The SAME fortnight the occupancy chart is showing. Switching the
            // Group control from "By area" to "By money" was jumping the phone
            // straight back to ninety-odd columns and dropping the gesture with
            // it — the same Daily chart behaving as two different charts.
            days={moneyDays}
            expectedDays={daysInWindow(chartViewWindow.from, chartViewWindow.to)}
            pending={money.state.phase === 'loading' || chartStepping}
            unavailable={money.state.phase === 'error'}
            onSwipe={phone ? pageChart : undefined}
          />
        ) : (
          <BookingsCharts
            bookings={chartBookings}
            chartType={chartType}
            group={groupBy}
            viewWindow={chartViewWindow}
            pending={chartStepping}
            onSwipe={phone ? pageChart : undefined}
          />
        )}
      </div>

      {modal && (
        <OwnerBookingModal
          bookingId={modal.bookingId}
          ownerId={modal.ownerId}
          initialMode={modal.initialMode}
          createOwner={modal.createOwner}
          createDefaults={modal.convertingViewing ? viewingToOwnerDefaults(modal.convertingViewing) : undefined}
          onHouseholdCreated={modal.convertingViewing ? onHouseholdCreated(modal.convertingViewing) : undefined}
          viewing={modal.viewing}
          onBookingCreated={modal.convertingViewing ? onBookingCreatedFor(modal.convertingViewing) : undefined}
          seed={bookings.find((b) => b.id === modal.bookingId) ?? null}
          today={today}
          onClose={closeModal}
          onChanged={onModalChanged}
        />
      )}


      {editHelper && (
        <StaffModal
          staff={staff}
          today={today}
          helperId={editHelper.id}
          onClose={() => setEditHelper(null)}
          onChanged={reloadStaff}
        />
      )}
    </div>
  );
}
